/**
 * The Exi Git icon set.
 *
 * Drawn here rather than installed. An icon library is a few hundred kilobytes
 * and a licence obligation for the thirty glyphs an application actually uses,
 * and the shapes are part of the product's identity in the same way the type
 * scale is — DESIGN_LANGUAGE.md asks for an owned visual language, and borrowed
 * icons are the fastest way to look like whichever product they came from.
 *
 * The rules every glyph in here follows, so that a new one matches without
 * anybody having to measure:
 *
 *   - A 16×16 viewBox. The interface is dense; 16px is the working size and a
 *     glyph designed at 24 and scaled down loses its stroke alignment.
 *   - 1.5 stroke, round caps, round joins, no fill. Set on `<svg>` once rather
 *     than per path.
 *   - Geometry on the half-pixel grid (x.5) so a 1.5 stroke lands on whole
 *     pixels at 100% zoom instead of smearing across two.
 *   - `currentColor`, always. An icon is the colour of the text beside it.
 *   - `aria-hidden` by default. An icon that carries meaning on its own takes a
 *     `label`, which turns it into `role="img"` with an accessible name; §8
 *     forbids conveying state by appearance alone, and that is the mechanism.
 */

import type { SVGProps } from 'react';

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'children'> {
  /** Pixel size. The default matches the dense default control height. */
  size?: number;
  /**
   * An accessible name. Supply one only when the icon is the *only* carrier of
   * its meaning; an icon beside a visible label must stay `aria-hidden`, or a
   * screen reader announces the label twice.
   */
  label?: string;
}

function Icon({ size = 16, label, children, ...rest }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

/* --- navigation and chrome ------------------------------------------- */

export const ChevronRight = (p: IconProps) => (
  <Icon {...p}>
    <path d="M6 3.5 10.5 8 6 12.5" />
  </Icon>
);

export const ChevronDown = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3.5 6 8 10.5 12.5 6" />
  </Icon>
);

export const ChevronLeft = (p: IconProps) => (
  <Icon {...p}>
    <path d="M10 3.5 5.5 8 10 12.5" />
  </Icon>
);

export const ChevronUpDown = (p: IconProps) => (
  <Icon {...p}>
    <path d="M5 6.5 8 3.5l3 3M5 9.5l3 3 3-3" />
  </Icon>
);

export const Close = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 4l8 8M12 4l-8 8" />
  </Icon>
);

export const Search = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="7" cy="7" r="3.75" />
    <path d="M9.75 9.75 13 13" />
  </Icon>
);

export const MoreHorizontal = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="3.5" cy="8" r=".75" fill="currentColor" stroke="none" />
    <circle cx="8" cy="8" r=".75" fill="currentColor" stroke="none" />
    <circle cx="12.5" cy="8" r=".75" fill="currentColor" stroke="none" />
  </Icon>
);

export const ExternalLink = (p: IconProps) => (
  <Icon {...p}>
    <path d="M9.5 3.5H12.5V6.5M12.5 3.5 8 8" />
    <path d="M12.5 9.5v2a1 1 0 0 1-1 1h-7a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1h2" />
  </Icon>
);

/* --- the product's own vocabulary ------------------------------------ */

/**
 * The Exi Git mark.
 *
 * Three nodes on a vertical and one branching away and rejoining: a commit
 * line that diverged and came back. It is the lifecycle the product is named
 * for — CODE, COLLABORATE, SECURE, VERIFY, DEPLOY — compressed into the one
 * shape every engineer reads without being told what it is. Provisional along
 * with the rest of the brand (R-022).
 */
export const Mark = ({ size = 16, label, ...rest }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
    focusable="false"
    {...rest}
  >
    <path d="M4.5 2.5v11" />
    <path d="M4.5 5.5h4a3 3 0 0 1 3 3v0a3 3 0 0 1-3 3h-4" />
    <circle cx="4.5" cy="2.5" r="1.25" fill="currentColor" stroke="none" />
    <circle cx="11.5" cy="8.5" r="1.25" fill="currentColor" stroke="none" />
    <circle cx="4.5" cy="13.5" r="1.25" fill="currentColor" stroke="none" />
  </svg>
);

export const Repository = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3.5 3.25A1.75 1.75 0 0 1 5.25 1.5h7.25v10H5.25a1.75 1.75 0 0 0-1.75 1.75z" />
    <path d="M3.5 13.25a1.75 1.75 0 0 0 1.75 1.25h7.25v-3" />
  </Icon>
);

export const Branch = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4.5 4.75v6.5" />
    <path d="M11.5 6.25v.5a2.5 2.5 0 0 1-2.5 2.5H7a2.5 2.5 0 0 0-2.5 2.5" />
    <circle cx="4.5" cy="3.25" r="1.5" />
    <circle cx="11.5" cy="4.75" r="1.5" />
    <circle cx="4.5" cy="12.75" r="1.5" />
  </Icon>
);

export const PullRequest = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4.5 5.75v5" />
    <circle cx="4.5" cy="4.25" r="1.5" />
    <circle cx="4.5" cy="12.25" r="1.5" />
    <circle cx="11.5" cy="12.25" r="1.5" />
    <path d="M11.5 10.75V6.5a2 2 0 0 0-2-2H7.5" />
    <path d="M9.25 2.75 7.5 4.5l1.75 1.75" />
  </Icon>
);

export const Run = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="8" cy="8" r="6" />
    <path d="M6.75 5.75 10.5 8l-3.75 2.25z" fill="currentColor" />
  </Icon>
);

export const Shield = (p: IconProps) => (
  <Icon {...p}>
    <path d="M8 1.75 3 3.5v4.25c0 3 2.1 5.4 5 6.5 2.9-1.1 5-3.5 5-6.5V3.5z" />
  </Icon>
);

export const ShieldAlert = (p: IconProps) => (
  <Icon {...p}>
    <path d="M8 1.75 3 3.5v4.25c0 3 2.1 5.4 5 6.5 2.9-1.1 5-3.5 5-6.5V3.5z" />
    <path d="M8 5.5v3" />
    <circle cx="8" cy="10.75" r=".75" fill="currentColor" stroke="none" />
  </Icon>
);

export const Activity = (p: IconProps) => (
  <Icon {...p}>
    <path d="M1.5 8h3L6.5 3.5 9.5 12.5l2-4.5h3" />
  </Icon>
);

export const Bell = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 7a4 4 0 0 1 8 0c0 2.5.75 3.5 1.25 4h-10.5C3.25 10.5 4 9.5 4 7z" />
    <path d="M6.5 13a1.6 1.6 0 0 0 3 0" />
  </Icon>
);

export const Star = (p: IconProps) => (
  <Icon {...p}>
    <path d="m8 2 1.85 3.9 4.15.6-3 3 .71 4.25L8 11.75 4.29 13.75 5 9.5l-3-3 4.15-.6z" />
  </Icon>
);

export const StarFilled = (p: IconProps) => (
  <Icon {...p}>
    <path
      d="m8 2 1.85 3.9 4.15.6-3 3 .71 4.25L8 11.75 4.29 13.75 5 9.5l-3-3 4.15-.6z"
      fill="currentColor"
    />
  </Icon>
);

export const Watch = (p: IconProps) => (
  <Icon {...p}>
    <path d="M1.5 8S3.75 3.5 8 3.5 14.5 8 14.5 8 12.25 12.5 8 12.5 1.5 8 1.5 8z" />
    <circle cx="8" cy="8" r="2" />
  </Icon>
);

export const Lock = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3.5" y="7" width="9" height="6.5" rx="1" />
    <path d="M5.5 7V4.75a2.5 2.5 0 0 1 5 0V7" />
  </Icon>
);

export const Globe = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="8" cy="8" r="6" />
    <path d="M2.25 6.5h11.5M2.25 9.5h11.5" />
    <path d="M8 2a9 9 0 0 1 0 12A9 9 0 0 1 8 2z" />
  </Icon>
);

export const Organization = (p: IconProps) => (
  <Icon {...p}>
    <path d="M2.5 13.5h11" />
    <path d="M3.5 13.5V3a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 .5.5v10.5" />
    <path d="M9.5 13.5V6.5h3a.5.5 0 0 1 .5.5v6.5" />
    <path d="M5.5 5h2M5.5 7.5h2M5.5 10h2" />
  </Icon>
);

/* --- state ------------------------------------------------------------ */

export const Check = (p: IconProps) => (
  <Icon {...p}>
    <path d="m3.5 8.5 3 3 6-7" />
  </Icon>
);

export const CheckCircle = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="8" cy="8" r="6" />
    <path d="m5.25 8.25 2 2 3.5-4" />
  </Icon>
);

export const AlertTriangle = (p: IconProps) => (
  <Icon {...p}>
    <path d="M8 2.25 14.25 13H1.75z" />
    <path d="M8 6.5v3" />
    <circle cx="8" cy="11.25" r=".75" fill="currentColor" stroke="none" />
  </Icon>
);

export const AlertOctagon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M5.5 1.75h5L14.25 5.5v5L10.5 14.25h-5L1.75 10.5v-5z" />
    <path d="M8 4.75v3.5" />
    <circle cx="8" cy="10.75" r=".75" fill="currentColor" stroke="none" />
  </Icon>
);

export const Info = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="8" cy="8" r="6" />
    <path d="M8 7.5v3.5" />
    <circle cx="8" cy="5.25" r=".75" fill="currentColor" stroke="none" />
  </Icon>
);

export const Clock = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="8" cy="8" r="6" />
    <path d="M8 4.75V8l2.25 1.5" />
  </Icon>
);

export const Dot = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="8" cy="8" r="3.25" fill="currentColor" stroke="none" />
  </Icon>
);

export const Dash = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 8h8" />
  </Icon>
);

export const Slash = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="8" cy="8" r="6" />
    <path d="m4.25 11.75 7.5-7.5" />
  </Icon>
);

/* --- settings and identity -------------------------------------------- */

export const Settings = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="8" cy="8" r="2.25" />
    <path d="M8 1.5v1.75M8 12.75v1.75M14.5 8h-1.75M3.25 8H1.5M12.6 3.4l-1.25 1.25M4.65 11.35 3.4 12.6M12.6 12.6l-1.25-1.25M4.65 4.65 3.4 3.4" />
  </Icon>
);

export const Person = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="8" cy="5.5" r="2.75" />
    <path d="M2.75 14a5.25 5.25 0 0 1 10.5 0" />
  </Icon>
);

export const SignOut = (p: IconProps) => (
  <Icon {...p}>
    <path d="M6 13.5H3.5a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1H6" />
    <path d="M10.25 10.75 13 8l-2.75-2.75M13 8H6" />
  </Icon>
);

export const Key = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="5" cy="11" r="2.75" />
    <path d="m7 9 6.5-6.5M11 5l1.5 1.5M9.25 6.75l1.5 1.5" />
  </Icon>
);

export const Sun = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="8" cy="8" r="3" />
    <path d="M8 1.5v1.25M8 13.25v1.25M14.5 8h-1.25M2.75 8H1.5M12.6 3.4l-.9.9M4.3 11.7l-.9.9M12.6 12.6l-.9-.9M4.3 4.3l-.9-.9" />
  </Icon>
);

export const Moon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M13.25 9.5A5.75 5.75 0 0 1 6.5 2.75a5.75 5.75 0 1 0 6.75 6.75z" />
  </Icon>
);

export const Monitor = (p: IconProps) => (
  <Icon {...p}>
    <rect x="1.75" y="2.75" width="12.5" height="8.5" rx="1" />
    <path d="M5.5 14h5M8 11.25V14" />
  </Icon>
);

export const Rows = (p: IconProps) => (
  <Icon {...p}>
    <path d="M2 4.5h12M2 8h12M2 11.5h12" />
  </Icon>
);
